// Lógica de export do Acompanhamento (PDF / HTML / CSV rows)
// Extraído de AcompanhamentoFullscreen.js para manter o componente focado em UI.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { escapeHtml, formatHoraExport, formatDuracaoExport } from '../utils/format';

/**
 * Constrói as linhas (estruturadas) de export a partir do payload de acompanhamento.
 * @param {Object} acompanharData payload do endpoint /ofs/{id}/acompanhamento
 * @param {Array} lotesFiltrados (opcional) sub-conjunto de lotes a exportar; se vazio usa todos.
 * @returns {Array<Object>} linhas com chaves: lote, operacao, estado, qtLote, qtFeita, perc, participantes, inicio, fim, duracao, eficiencia, defeitos.
 */
export function buildExportRows(acompanharData, lotesFiltrados = []) {
  const lotes = (lotesFiltrados && lotesFiltrados.length > 0)
    ? lotesFiltrados
    : (acompanharData?.lotes || []);
  const ops = acompanharData?.operacoes || [];
  const rows = [];
  lotes.forEach(lote => {
    const loteOps = lote.operacoes_estado || [];
    loteOps.forEach(op => {
      const opInfo = ops.find(o => o.operacao_id === op.operacao_id);
      const qtLote = lote.quantidade_ajustada || lote.quantidade || 0;
      rows.push({
        lote: lote.codigo_lote || '',
        categoria: lote.categoria_nome || '',
        operacao: `${op.codigo || opInfo?.codigo || ''} ${op.nome || opInfo?.nome || ''}`.trim(),
        estado: op.status === 'concluida' ? 'Concluida' : op.status === 'parcial' ? 'Parcial' : 'Pendente',
        estadoCor: op.status === 'concluida' ? '#16a34a' : op.status === 'parcial' ? '#d97706' : '#94a3b8',
        qtLote,
        qtFeita: op.quantidade_produzida || 0,
        perc: qtLote > 0 ? Math.round((op.quantidade_produzida / qtLote) * 100) : 0,
        participantes: opInfo?.participantes?.map(p => p.nome).join(', ') || '—',
        inicio: formatHoraExport(op.primeiro_inicio || opInfo?.primeiro_inicio),
        fim: formatHoraExport(op.ultimo_fim || opInfo?.ultimo_fim),
        duracao: formatDuracaoExport(op.tempo_segundos || opInfo?.tempo_total_segundos),
        eficiencia: (op.eficiencia != null ? op.eficiencia : opInfo?.eficiencia) != null ? `${op.eficiencia ?? opInfo?.eficiencia}%` : '—',
        defeitos: op.defeitos || 0,
      });
    });
  });
  return rows;
}

/**
 * Constrói HTML completo do relatório (para impressão/download).
 */
export function buildReportHtml({ acompanharData, lotesFiltrados, numeroOf, marca, dataEntrega }) {
  const resumo = acompanharData?.resumo || {};
  const rows = buildExportRows(acompanharData, lotesFiltrados);
  const tableRowsHtml = rows.map(r =>
    `<tr><td>${escapeHtml(r.lote)}</td><td>${escapeHtml(r.operacao)}</td>` +
    `<td style="color:${r.estadoCor};font-weight:600">${escapeHtml(r.estado)}</td>` +
    `<td style="text-align:center">${r.qtLote}</td><td style="text-align:center">${r.qtFeita}</td>` +
    `<td style="text-align:center">${r.perc}%</td><td>${escapeHtml(r.participantes)}</td>` +
    `<td>${escapeHtml(r.inicio)}</td><td>${escapeHtml(r.fim)}</td>` +
    `<td style="text-align:center">${escapeHtml(r.duracao)}</td>` +
    `<td style="text-align:center;font-weight:600;color:${r.eficiencia !== '—' && parseFloat(r.eficiencia) >= 100 ? '#16a34a' : '#d97706'}">${escapeHtml(r.eficiencia)}</td>` +
    `<td style="text-align:center">${r.defeitos}</td></tr>`
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Acompanhamento ${escapeHtml(numeroOf || '')}</title>` +
    `<style>*{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:20px;color:#1e293b;font-size:12px}` +
    `h1{font-size:18px;margin:0 0 4px 0}h2{font-size:12px;color:#64748b;margin:0 0 16px 0}` +
    `.summary{display:flex;gap:16px;margin-bottom:16px;flex-wrap:wrap}` +
    `.summary-item{background:#f8fafc;padding:10px 14px;border-radius:6px;border:1px solid #e2e8f0;text-align:center}` +
    `.summary-item .value{font-size:18px;font-weight:700}.summary-item .label{font-size:10px;color:#64748b;margin-top:2px}` +
    `table{width:100%;border-collapse:collapse;font-size:10px;page-break-inside:auto}` +
    `tr{page-break-inside:avoid;page-break-after:auto}` +
    `th{background:#1e293b;color:white;padding:6px 5px;text-align:left;font-weight:600;white-space:nowrap;font-size:9px}` +
    `td{padding:4px 5px;border-bottom:1px solid #e2e8f0}tr:nth-child(even){background:#f8fafc}` +
    `.footer{margin-top:16px;font-size:9px;color:#94a3b8;text-align:center;padding-top:8px;border-top:1px solid #e2e8f0}` +
    `@media print{body{margin:8px;padding:8px}}</style></head><body>` +
    `<h1>Acompanhamento OF ${escapeHtml(numeroOf || '')}</h1>` +
    `<h2>${escapeHtml(marca || '')}${dataEntrega ? ` | Entrega: ${new Date(dataEntrega).toLocaleDateString('pt-PT')}` : ''}</h2>` +
    `<div class="summary">` +
    `<div class="summary-item"><div class="value">${resumo.percentagem_concluida || 0}%</div><div class="label">Concluido</div></div>` +
    `<div class="summary-item"><div class="value">${acompanharData?.lotes?.length || 0}</div><div class="label">Lotes</div></div>` +
    `<div class="summary-item"><div class="value">${resumo.total_anomalias || 0}</div><div class="label">Anomalias</div></div>` +
    `<div class="summary-item"><div class="value">${resumo.operadores_participaram || 0}</div><div class="label">Operadores</div></div>` +
    `</div><table><thead><tr>` +
    `<th>Lote</th><th>Operacao</th><th>Estado</th>` +
    `<th>Qt Lote</th><th>Qt Feita</th><th>%</th><th>Operador(es)</th>` +
    `<th>Inicio</th><th>Fim</th><th>Duracao</th><th>Efic.</th><th>Def.</th>` +
    `</tr></thead><tbody>${tableRowsHtml}</tbody></table>` +
    `<div class="footer">Gerado em ${new Date().toLocaleString('pt-PT')} | Samidel</div></body></html>`;
}

/**
 * Exporta o acompanhamento como PDF via jsPDF + autoTable.
 * Em caso de erro chama o fallback que recebe.
 */
export function exportAcompanhamentoPdf({ acompanharData, lotesFiltrados, numeroOf, marca, dataEntrega, onFallback }) {
  try {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const resumo = acompanharData?.resumo || {};
    const rows = buildExportRows(acompanharData, lotesFiltrados);

    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Acompanhamento OF ${numeroOf || ''}`, 14, 15);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    const subtitle = `${marca || ''}${dataEntrega ? ` | Entrega: ${new Date(dataEntrega).toLocaleDateString('pt-PT')}` : ''}`;
    doc.text(subtitle, 14, 21);

    // Summary boxes
    doc.setTextColor(30);
    const summaryItems = [
      { label: 'Concluído', value: `${resumo.percentagem_concluida || 0}%` },
      { label: 'Lotes', value: `${acompanharData?.lotes?.length || 0}` },
      { label: 'Anomalias', value: `${resumo.total_anomalias || 0}` },
      { label: 'Operadores', value: `${resumo.operadores_participaram || 0}` },
    ];
    let xPos = 14;
    summaryItems.forEach(item => {
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(xPos, 25, 35, 14, 2, 2, 'F');
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(item.value, xPos + 17.5, 33, { align: 'center' });
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(item.label, xPos + 17.5, 37, { align: 'center' });
      doc.setTextColor(30);
      xPos += 40;
    });

    // Table data
    const tableHead = [['Lote', 'Operação', 'Estado', 'Qt Lote', 'Qt Feita', '%', 'Operador(es)', 'Início', 'Fim', 'Duração', 'Efic.', 'Def.']];
    const tableBody = rows.map(r => [
      r.lote, r.operacao, r.estado, r.qtLote, r.qtFeita, `${r.perc}%`,
      r.participantes, r.inicio, r.fim, r.duracao, r.eficiencia, r.defeitos
    ]);

    autoTable(doc, {
      startY: 43,
      head: tableHead,
      body: tableBody,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], fontSize: 7, fontStyle: 'bold', halign: 'left' },
      bodyStyles: { fontSize: 7, cellPadding: 1.5 },
      columnStyles: {
        3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' },
        9: { halign: 'center' }, 10: { halign: 'center' }, 11: { halign: 'center' },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 8, right: 8 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 2) {
          const estado = data.cell.raw || '';
          if (estado.includes('Conclu')) data.cell.styles.textColor = [22, 163, 74];
          else if (estado.includes('Parci') || estado.includes('Em Curso')) data.cell.styles.textColor = [217, 119, 6];
          else if (estado.includes('Não')) data.cell.styles.textColor = [148, 163, 184];
        }
      }
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(`Gerado em ${new Date().toLocaleString('pt-PT')} | Samidel | Página ${i}/${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 5, { align: 'center' });
    }

    doc.save(`acompanhamento_${numeroOf || 'of'}.pdf`);
    return { ok: true };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Erro ao gerar PDF:', error);
    if (onFallback) onFallback(error);
    return { ok: false, error };
  }
}

/**
 * Faz download de HTML do relatório.
 */
export function downloadAcompanhamentoHtml({ acompanharData, lotesFiltrados, numeroOf, marca, dataEntrega }) {
  const htmlContent = buildReportHtml({ acompanharData, lotesFiltrados, numeroOf, marca, dataEntrega });
  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `acompanhamento_${numeroOf || 'of'}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
